import {
  AccessType,
  InvitableOrganization,
  InvitableUser,
  InvitedEntities,
  InvitedOrganization,
  InvitedUser
} from "@medeloop/medeloop-components-library/dist/components/ShareModal/types";
import { useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import {
  addOrganizationToDataSource,
  getOrganizationDatasources,
  getOrganizationResearchers,
  removeOrganizationFromDataSource
} from 'src/api/organizations';
import { getDataSourceResearchers, getOrganizationUnitsByDataSourceId } from 'src/api/data-sources';
import { OrganizationUnitsResponse } from "src/api/data-sources-dto";
import {
  DataSource,
  DataSourceOrganization,
  DataSourceResearcher,
  UserDataSourceRole,
} from 'src/types/data-sources';
import { addUserToDataSource, removeUserFromDataSource } from 'src/api/users';
import { showToast } from 'src/store/toast';


export const useShareDatasets = () => {

  const account = useSelector(selectAccount);
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();

  const [invitableResearchers, setInvitableResearchers] = useState<InvitableUser[]>([]);
  const [invitedResearchers, setInvitedResearchers] = useState<InvitedUser[]>([]);
  const [invitableOrganizations, setInvitableOrganizations] = useState<InvitableOrganization[]>([]);
  const [invitedOrganizations, setInvitedOrganizations] = useState<InvitedOrganization[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<DataSourceOrganization | DataSource | null>(null);
  const [fetchingShareResearchersData, setFetchingShareResearchersData] = useState(false);

  const fetchInvitableResearchers = async () => {
    const researchersMap = new Map<string, InvitableUser>();

    const resolvedResearchers = await Promise.all(
      account.organizationAdminIds.map((organizationId) =>
        getOrganizationResearchers({ token, organizationId }),
      ),
    );

    resolvedResearchers.forEach((researchers) => {
      researchers.researchers.forEach((researcher) => {
        if (!researchersMap.has(researcher.userId) && researcher.userId !== account.userId) {
          researchersMap.set(researcher.userId, {
            id: researcher.userId,
            email: researcher.researcherEmail,
            name: `${researcher.researcherGivenName ?? ''} ${researcher.researcherFamilyName ?? ''}`.trim(),
          });
        }
      });
    });

    setInvitableResearchers(Array.from(researchersMap.values()));
  };

  const fetchInvitedResearchers = async (dataSourceId: string) => {
    const invitedResearchers = await getDataSourceResearchers({
      token,
      dataSourceId,
    });

    const invitedEntities =
      invitedResearchers?.researchers.map((researcher: DataSourceResearcher) => ({
        id: researcher.userId,
        email: researcher.researcherEmail,
        name: `${researcher.researcherGivenName} ${researcher.researcherFamilyName}`,
        accessType: 'VIEW' as AccessType,
      })) ?? [];
    setInvitedResearchers(invitedEntities);
  };


  const fetchInvitedAndInvitableOrganizations = async (dataSourceId: string) => {

    const organizationUnits = await getOrganizationUnitsByDataSourceId({ token, dataSourceId }) as OrganizationUnitsResponse;

    const organizationDatasourcesInvited = await Promise.all(
      organizationUnits.data.organizationUnits.map((ou) =>
      getOrganizationDatasources({ token, organizationId: ou.organizationId })
        .then(orgDatasources => orgDatasources.dataSources)
      )
    ).then(results => results.flat());

    setInvitedOrganizations(
      organizationDatasourcesInvited
        .filter((orgDataSource) => orgDataSource.dataSourceId === dataSourceId)
        .map((orgDataSource) => ({
          id: orgDataSource.organizationId,
          displayName: orgDataSource.organizationDisplayName,
          accessType: 'VIEW' as AccessType,
        })),
    );

    setInvitableOrganizations(
      organizationUnits.data.organizationUnits.map((unit) => ({
        id: unit.organizationId,
        displayName: unit.displayName,
      }))
    );
  };


  const initShareModal = async (dataSource: DataSourceOrganization | DataSource) => {
    setFetchingShareResearchersData(true);
    setSelectedDataset(dataSource);

    await Promise.all([
      fetchInvitedResearchers(dataSource.dataSourceId),
      fetchInvitableResearchers(),
      fetchInvitedAndInvitableOrganizations(dataSource.dataSourceId),
    ]);

    setFetchingShareResearchersData(false);
  };

  const addResearchersToDataSource = async (users: InvitedUser[], dataSourceId: string) => {
    return await Promise.all(
      users.map((user) => {
        addUserToDataSource({
          token,
          userId: user.id,
          dataSourceId,
          role: UserDataSourceRole.RESEARCHER,
        });
      }),
    );
  };

  const removeResearchersFromDataSource = async (users: InvitedUser[], dataSourceId: string) => {
    return await Promise.all(
      users.map((user) => {
        removeUserFromDataSource({
          token,
          userId: user.id,
          dataSourceId,
          role: UserDataSourceRole.RESEARCHER,
        });
      }),
    );
  };

  const addOrganizationsToDataSource = async (organizations: InvitedOrganization[], dataSourceId: string) => {

    return await Promise.all(
      organizations.map((org) => {
        addOrganizationToDataSource({
          token,
          orgId: org.id,
          dataSourceId,
        });
      }),
    );
  };

  const removeOrganizationsFromDataSource = async (organizations: InvitedOrganization[], dataSourceId: string) => {
    return await Promise.all(
      organizations.map((org) => {
        removeOrganizationFromDataSource({
          token,
          orgId: org.id,
          dataSourceId,
        });
      }),
    );
  };

   const onShareSave = (recipients: InvitedEntities) => {
    if (!selectedDataset?.dataSourceId) {
      resetShareModalState();
      return;
    }

    const invitedUsers = invitedResearchers.map((user) => user.id);
    const invitedOrgs = invitedOrganizations.map((org) => org.id);

    const newUsers = recipients.users.filter((user) => !invitedUsers.includes(user.id));
    const newOrgs = recipients.organizations.filter(
      (org) => !invitedOrgs.includes(org.id),
    );

    const removedUsers = invitedResearchers.filter(
      (user) => !recipients.users.some((newUser) => newUser.id === user.id),
    );
    const removedOrgs = invitedOrganizations.filter(
      (org) => !recipients.organizations.some((newOrg) => newOrg.id === org.id),
    );

    try {
      if (newUsers.length > 0) {
        addResearchersToDataSource(newUsers, selectedDataset.dataSourceId);
      }
      if (removedUsers.length > 0) {
        removeResearchersFromDataSource(removedUsers, selectedDataset.dataSourceId);
      }
      if (newOrgs.length > 0) {
        addOrganizationsToDataSource(newOrgs, selectedDataset.dataSourceId);
      }
      if (removedOrgs.length > 0) {
        removeOrganizationsFromDataSource(removedOrgs, selectedDataset.dataSourceId);
      }

      dispatch(
        showToast({
          type: 'success',
          message: 'Dataset updated successfully',
        }),
      );
    } catch (error) {
      console.error('Error updating data source researchers:', error);
    }
    resetShareModalState();
  };

    const resetShareModalState = () => {
    setInvitedResearchers([]);
    setInvitableResearchers([]);
    setInvitableOrganizations([]);
    setInvitedOrganizations([]);
    setSelectedDataset(null);
  };


  return {
    invitableResearchers,
    invitedResearchers,
    invitableOrganizations,
    invitedOrganizations,
    fetchingShareResearchersData,
    onShareSave,
    resetShareModalState,
    initShareModal,
    fetchInvitedResearchers,
    fetchInvitedAndInvitableOrganizations
  };
}